import { FeaturedProject, FocusArea, Highlight, MinorProject, Role, SkillGroup } from '../types';
import { executorDiagram, foresightDiagram, leaderboardDiagram, roommateDiagram } from './diagrams';

// Text wrapped in **double asterisks** renders as emphasised (see Rich in components/ui.tsx).

export const profile = {
  name: 'Abhinav Garg',
  email: 'work.abhinavgarg@gmail.com',
  github: 'https://github.com/ABHINAVGARG05',
  linkedin: 'https://www.linkedin.com/in/abhinav--garg',
  resume: `${process.env.PUBLIC_URL}/Abhinav_Garg_Resume.pdf`,
  photo: `${process.env.PUBLIC_URL}/abhinav-portrait.jpg`,
  location: 'Vellore, India',
};

export const focusAreas: FocusArea[] = [
  {
    title: 'Distributed backends',
    body: 'Queues, workers and sandboxes that keep running under load — SQS fan-out, Asynq and BullMQ workers, rate limiting.',
    proof: ['Code Executor', 'Gravitas payments'],
  },
  {
    title: 'Real-time systems',
    body: 'Low-latency state with Redis, WebSockets and write-behind persistence that holds up at peak traffic.',
    proof: ['Leaderboard SDK', 'Roommate Dhoondho'],
  },
  {
    title: 'Applied AI',
    body: 'RAG, NL→SQL and LLM pipelines wired into real tools — and measured against ground truth before anyone relies on them.',
    proof: ['Samsung R&D', 'Foresight'],
  },
];

export const roles: Role[] = [
  {
    company: 'Caterpillar',
    role: 'Software Development Engineer Intern',
    period: 'Incoming',
    upcoming: true,
    summary: 'Accepted an offer to join as a Software Development Engineer intern.',
    points: [],
    stack: [],
  },
  {
    company: 'Samsung R&D Institute India',
    role: 'Project Intern — QA Tooling & Applied AI',
    period: 'Jul 2026 — Present',
    points: [
      'Architected a production **AI test management platform** for the mobile QA division — RBAC-gated test cases, execution runs and defect tracking, wired into **Jira and CI/CD**.',
      'Built a dual-mode **RAG pipeline for root-cause analysis**: semantic retrieval over failure logs, screenshots and SRS documents, paired with **NL→SQL** over structured defect records.',
      'Evaluated model output against ground-truth defects across **100+ test cases**, iterating on retrieval and prompting to raise SQL correctness before results reach QA engineers.',
    ],
    stack: ['Python', 'FastAPI', 'PostgreSQL', 'Vector DB', 'Qwen3-8B', 'Mistral', 'Jira', 'CI/CD'],
  },
  {
    company: 'SL4 Technology (SmartLinks)',
    role: 'Software Development Engineer Intern',
    period: 'Dec 2025 — Jul 2026',
    points: [
      'Engineered a **supply-chain load optimization engine** for a Transportation Management System — 3D bin-packing with OR-Tools and SCIP under placement, stacking and rotation constraints.',
      'Cut manual load-planning effort by **~70%** with auto-generated truck-level plans and a multi-view **3D visualization dashboard**.',
      'Shipped features through reviewed pull requests in a Git-based CI workflow on Linux.',
    ],
    stack: ['Python', 'FastAPI', 'PostgreSQL', 'OR-Tools', 'SCIP', 'AWS SQS'],
  },
];

export const featuredProjects: FeaturedProject[] = [
  {
    name: 'Remote Code Executor',
    kind: 'Distributed systems · Sandboxing',
    tagline: 'Runs untrusted Go and C++ submissions safely and asynchronously on AWS.',
    diagram: executorDiagram,
    specs: [
      { label: 'Memory', value: '256 MB' },
      { label: 'CPU', value: '1 core' },
      { label: 'Processes', value: '64 max' },
      { label: 'Network', value: 'None' },
      { label: 'Filesystem', value: 'Read-only' },
      { label: 'Output cap', value: '10 MB' },
    ],
    points: [
      'API gateway persists jobs to DynamoDB and fans them out over **AWS SQS** to language-specific Go and C++ executors.',
      'Each job runs in an **ephemeral Docker sandbox** — Linux namespaces and cgroups, dropped capabilities, non-root user — and is removed when it exits.',
      'Output lands in **S3** behind presigned result URLs; the gateway applies **token-bucket rate limiting** at 10 req/s per IP.',
    ],
    stack: ['Go', 'AWS SQS', 'DynamoDB', 'S3', 'EC2', 'Docker', 'Nginx', 'LocalStack'],
    links: [{ label: 'GitHub', href: 'https://github.com/ABHINAVGARG05/code-executor' }],
  },
  {
    name: 'Foresight',
    kind: 'Hackathon · Caterpillar',
    tagline: 'Offline, voice-first safety assistant for hydraulic excavator operators.',
    specs: [
      { label: 'Near-misses warned', value: '100%' },
      { label: 'False alarms', value: '−40% vs rules' },
      { label: 'Decision latency', value: '4.6 ms p50' },
      { label: 'Guardrail coverage', value: '100%' },
      { label: 'Simulation', value: '10 Hz' },
      { label: 'Tests', value: '250+' },
    ],
    specsNote: 'Measured on held-out synthetic data.',
    diagram: foresightDiagram,
    points: [
      'Rules make every safety decision; an **XGBoost risk model** can raise an alert but never lower one — **40% fewer false alarms** than the physics baseline at equal recall.',
      'Every alert is appended to a **SHA-256 hash-chained incident log**; analytics turn each shift into calibrated P10–P90 task-time estimates and coaching.',
      'Four isolated modules in one FastAPI process with a WebSocket live feed; voice and Q&A fall back **Groq → Ollama → local agent**, so the safety path never needs a network.',
    ],
    stack: ['Python', 'FastAPI', 'XGBoost', 'WebSockets', 'SQLite', 'TypeScript', 'Whisper / Vosk'],
    links: [{ label: 'GitHub', href: 'https://github.com/ABHINAVGARG05/Hack' }],
  },
  {
    name: 'Roommate Dhoondho',
    kind: 'Production · Live',
    diagram: roommateDiagram,
    tagline: 'Roommate matching for VIT students during hostel room counselling.',
    specs: [
      { label: 'Users', value: '1,600+' },
      { label: 'Peak load', value: '400+ concurrent' },
      { label: 'Auth', value: 'Google OAuth + JWT' },
      { label: 'Realtime', value: 'Socket.IO' },
      { label: 'Email', value: 'Brevo · Resend' },
      { label: 'API', value: 'Rate-limited' },
    ],
    points: [
      'Matched students on **location and lifestyle preferences** in the one window when the whole batch needed it at once.',
      'Served **400+ concurrent users** at peak and **1,600+ sign-ups** overall.',
      '**Google OAuth + JWT** auth, rate-limited Express APIs, a Socket.IO real-time layer and transactional email.',
    ],
    stack: ['MongoDB', 'Express', 'React', 'Node.js', 'Socket.IO', 'Passport'],
    links: [
      { label: 'Live', href: 'https://rd-tau.vercel.app' },
      { label: 'GitHub', href: 'https://github.com/ABHINAVGARG05/RD' },
    ],
  },
  {
    name: 'Real-Time Leaderboard SDK',
    kind: 'Open source · npm package',
    tagline: 'Drop-in real-time leaderboards for games and coding contests.',
    diagram: leaderboardDiagram,
    specs: [
      { label: 'Hot path', value: 'Redis' },
      { label: 'Storage', value: 'PG · MySQL · Mongo' },
      { label: 'Transport', value: 'REST + Socket.IO' },
      { label: 'Writes', value: 'Write-behind' },
      { label: 'Queries', value: 'Top-N · rank' },
      { label: 'Language', value: 'TypeScript' },
    ],
    points: [
      'Redis serves low-latency score updates and rank reads, with **pluggable persistence** across PostgreSQL, MySQL and MongoDB.',
      'Ships an **Express REST router + Socket.IO** layer for score submission, top-N queries, rank lookup and live broadcasts.',
      '**Write-behind batching**, input validation and a (game_id, score desc) index keep the hot path fast and storage cheap.',
    ],
    stack: ['TypeScript', 'Redis', 'PostgreSQL', 'MySQL', 'MongoDB', 'Socket.IO', 'Express'],
    links: [
      { label: 'npm', href: 'https://www.npmjs.com/package/live-leaderboard' },
      { label: 'GitHub', href: 'https://github.com/ABHINAVGARG05/Live-Leaderboard' },
    ],
  },
];

export const minorProjects: MinorProject[] = [
  {
    name: 'Mailer',
    year: '2025',
    description: 'Bulk email campaign API — CSV and template upload, BullMQ workers with batching, retries and exponential backoff, live campaign status.',
    stack: ['TypeScript', 'BullMQ', 'Redis'],
    href: 'https://github.com/ABHINAVGARG05/Mailer_ts',
  },
  {
    name: 'RL Cloud Scheduler',
    year: '2026',
    description: 'Dueling DQN agent that places jobs across a simulated CPU/memory cluster, benchmarked against First-Fit, Best-Fit and Round-Robin.',
    stack: ['PyTorch', 'Gymnasium'],
    href: 'https://github.com/ABHINAVGARG05/RL',
  },
  {
    name: 'Explainable Fraud Detection',
    year: '2026',
    description: 'XGBoost fraud detector with drift-aware SHAP monitoring, DiCE counterfactuals and an append-only audit log.',
    stack: ['XGBoost', 'SHAP', 'Streamlit'],
    href: 'https://github.com/ABHINAVGARG05/XAI-PROJECT',
  },
  {
    name: 'TradeAxis',
    year: '2024',
    description: 'Trading platform backend — order execution and portfolio analytics APIs in Go.',
    stack: ['Go (Fiber)', 'PostgreSQL', 'Docker'],
    href: 'https://github.com/ABHINAVGARG05/trading-platform',
  },
];

export const leadership: Highlight[] = [
  {
    title: 'Technical Manager',
    org: 'Riviera & Gravitas, VIT Vellore',
    period: '2026',
    description:
      'Headed the tech team for VIT’s two flagship fests — Riviera, the cultural fest, and Gravitas, the tech fest. Planned and delegated the team’s work, acted as the tech point of contact for the organising and event teams, and mentored juniors into owning parts of the stack.',
  },
  {
    title: 'Technical Head',
    org: 'Mozilla Firefox Club, VIT',
    period: 'Mar 2025 — Jan 2026',
    description: 'Led a 25-member technical team, owning code review and delivery for 10+ event websites while mentoring juniors on frontend and backend.',
  },
  {
    title: 'Technical Student Coordinator',
    org: 'Gravitas ’25, VIT Vellore',
    period: 'Jul — Sep 2025',
    description: 'Built the PayU payment integration in Go with Redis-backed Asynq queues and timed seat locking — load-tested to 150+ req/s across 1,000+ concurrent registrations.',
  },
];

export const recognition: Highlight[] = [
  {
    title: 'Finalist, Code for Good ’25',
    org: 'JPMorgan Chase',
    description: 'Placed top 2 of 9 teams (72 overall) with a full-stack NGO platform built in 24 hours.',
  },
  {
    title: 'SDE Intern offer (6 months)',
    org: 'Amazon',
    description: 'Received a six-month Software Development Engineer internship offer.',
  },
  {
    title: 'Academic Excellence Award',
    org: 'VIT Vellore',
    description: 'Awarded for top academic standing — 9.34 CGPA across three consecutive years.',
  },
];

export const skills: SkillGroup[] = [
  { label: 'Languages', items: ['Go', 'Python', 'TypeScript', 'JavaScript', 'Java', 'C++', 'C', 'SQL'] },
  { label: 'Backend', items: ['FastAPI', 'Node.js', 'Express', 'Fiber', 'Socket.IO', 'BullMQ', 'Asynq', 'REST APIs'] },
  { label: 'Data', items: ['PostgreSQL', 'MySQL', 'MongoDB', 'DynamoDB', 'Redis', 'Vector DBs'] },
  { label: 'Cloud & infra', items: ['AWS (SQS, S3, EC2, KMS)', 'Docker', 'Terraform', 'Nginx', 'Linux namespaces & cgroups', 'CI/CD', 'Git'] },
  { label: 'AI & optimization', items: ['RAG', 'NL→SQL', 'LLM evaluation', 'LangGraph', 'PyTorch', 'XGBoost', 'scikit-learn', 'OR-Tools', 'SCIP'] },
];
